// web/src/scene/rtls/AssetSelectionEffect.tsx
//
// Invisible scene component. Watches Redux `selectedAssetId` and calls
// sceneRef.camera.flyTo whenever the selection changes. Lives inside the
// R3F canvas so useSceneRef() resolves (the outer SceneRefContext is in
// App.tsx, re-provided inside Canvas.tsx for R3F's isolated React root).

import { useEffect, useRef } from 'react'
import { useAppSelector } from '../../app/useAppSelector'
import { useSceneRef } from '../SceneRefContext'

export function AssetSelectionEffect() {
  const sceneRef = useSceneRef()
  const selectedAssetId = useAppSelector(s => s.rtls.selectedAssetId)
  const assets = useAppSelector(s => s.rtls.assets)
  const playbackMode = useAppSelector(s => s.rtls.playbackMode)
  const lastSelectedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!selectedAssetId) return
    // During playback, CameraController owns camera movement (flyTo +
    // follow-path). Skip the live-position fly-to here to avoid fighting.
    if (playbackMode === 'playback') return
    // Skip if the same id fires twice in a row (Redux re-render without an
    // actual selection change — guards against fly-to spam).
    if (lastSelectedRef.current === selectedAssetId) return

    const asset = assets[selectedAssetId]
    if (!asset) return

    // Only mark as done AFTER the fly-to has actually fired. If sceneRef is
    // still null (first-render race before Canvas mounts), we leave
    // lastSelectedRef unchanged so the next render retries.
    if (!sceneRef.current) return

    const viewDistance = asset.type === 'equipment' ? 200 : 150
    // Approach direction: equipment GLBs have forward → -Y after the
    // AssetMarker rotation bake, so camera parks at +Y to see the front.
    // Personnel (miner.glb) has its forward authored the opposite way,
    // so the camera needs to approach from -Y to see the front instead.
    const approachDir =
      asset.type === 'personnel'
        ? { x: 0, y: -0.85, z: 0.5 }
        : { x: 0, y: 0.85, z: 0.5 }
    // duration is in SECONDS (not ms) — CameraController divides the
    // per-frame delta (also seconds) by this value. Passing 1200 would make
    // the animation ~20 minutes long and leave OrbitControls disabled.
    sceneRef.current.camera.flyTo(asset.position, viewDistance, 1.2, approachDir)
    lastSelectedRef.current = selectedAssetId
  }, [selectedAssetId, assets, sceneRef, playbackMode])

  useEffect(() => {
    if (!selectedAssetId) lastSelectedRef.current = null
  }, [selectedAssetId])

  return null
}
